import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../../company-management/constants/permissions';
import { DEV_TOOLS_PATH, isDevToolsEnabled } from '../../../dev-tools/constants';
import {
  DashboardIcon,
  CalendarIcon,
  ArchiveIcon,
  PeopleIcon,
  ManageAccountsIcon,
  SettingsIcon,
  SupportIcon,
  LogoutIcon,
  DevToolsIcon,
} from '../icons/NavIcons';
import './OverviewSideNav.css';
import { useAppEmailVerificationRestricted } from '../../email-verification/EmailVerificationRestrictionContext';
import { APP_EMAIL_VERIFICATION_LOCK_TITLE } from '../../email-verification/appEmailVerificationRestriction';
import '../../email-verification/appEmailVerificationLock.css';

const NavLink = ({
  icon: Icon,
  label,
  caption,
  onClick,
  isActive,
  badge,
  variant,
  disabled = false,
  allowed = false,
}) => (
  <button
    type="button"
    className={`overview-side-nav__link${isActive ? ' overview-side-nav__link--active' : ''}${
      variant === 'signOut' ? ' overview-side-nav__link--sign-out' : ''
    }${variant === 'support' ? ' overview-side-nav__link--support' : ''}${
      disabled ? ' overview-side-nav__link--disabled' : ''
    }${allowed ? ' overview-side-nav__link--allowed' : ''}`}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    aria-label={label}
    title={disabled ? APP_EMAIL_VERIFICATION_LOCK_TITLE : label}
    aria-current={isActive ? 'page' : undefined}
  >
    <span className="overview-side-nav__icon-wrap" aria-hidden>
      <Icon />
      {badge != null && badge > 0 ? (
        <span className="overview-side-nav__badge">{badge > 99 ? '99+' : badge}</span>
      ) : null}
    </span>
    <span className="overview-side-nav__caption" aria-hidden>
      {caption}
    </span>
  </button>
);

/**
 * Fixed bottom bar — same destinations as NavigationMenu (plus sign out).
 * Icon + short caption; full names on `aria-label` and `title`.
 */
const OverviewSideNav = ({ meetingCount = 0, onSignOut }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const navigationLocked = useAppEmailVerificationRestricted();
  const showDevTools = isDevToolsEnabled(user?.isSpecialAdmin);

  const path = location.pathname;

  const canOverview = hasPermission(PERMISSIONS.OVERVIEW.VIEW);
  if (!canOverview) return null;

  const isOverviewActive =
    path === '/app/overview' || path.startsWith('/app/overview/meetings');

  const items = [
    {
      key: 'overview',
      label: 'Overview',
      caption: 'Overview',
      icon: DashboardIcon,
      path: '/app/overview',
      permission: PERMISSIONS.OVERVIEW.VIEW,
      isActive: isOverviewActive,
      meetingCount,
    },
    {
      key: 'calendar',
      label: 'Calendar',
      caption: 'Calendar',
      icon: CalendarIcon,
      path: '/app/overview/calendar',
      permission: PERMISSIONS.CALENDAR.VIEW,
      isActive: path.startsWith('/app/overview/calendar'),
    },
    {
      key: 'history',
      label: 'History',
      caption: 'History',
      icon: ArchiveIcon,
      path: '/app/overview/history',
      permission: PERMISSIONS.HISTORY.VIEW,
      isActive: path.startsWith('/app/overview/history'),
    },
    {
      key: 'clients',
      label: 'Client Management',
      caption: 'Clients',
      icon: PeopleIcon,
      path: '/app/overview/clients',
      permission: PERMISSIONS.CLIENTS.VIEW,
      isActive: path.startsWith('/app/overview/clients'),
    },
    {
      key: 'employees',
      label: 'Employee Management',
      caption: 'Employees',
      icon: ManageAccountsIcon,
      path: '/app/overview/company-management',
      permission: PERMISSIONS.EMPLOYEES.VIEW,
      isActive:
        path.startsWith('/app/overview/company-management') ||
        path === '/app/overview/permissions',
    },
    {
      key: 'settings',
      label: 'Settings',
      caption: 'Settings',
      icon: SettingsIcon,
      path: '/app/overview/settings',
      permission: PERMISSIONS.SETTINGS.VIEW,
      isActive: path.startsWith('/app/overview/settings'),
    },
  ];

  const canSupport = hasPermission(PERMISSIONS.SUPPORT.VIEW);
  const isSupportActive = path.startsWith('/app/overview/support');
  const isDevToolsActive = path.startsWith(DEV_TOOLS_PATH);

  const visible = items.filter((item) => hasPermission(item.permission));

  return (
    <nav
      className={`overview-side-nav${navigationLocked ? ' overview-side-nav--locked' : ''}`}
      aria-label="Main navigation"
    >
      <div className="overview-side-nav__inner">
        <div className="overview-side-nav__list" role="presentation">
          {visible.map((item) => (
            <NavLink
              key={item.key}
              icon={item.icon}
              label={item.label}
              caption={item.caption}
              isActive={item.isActive}
              disabled={navigationLocked && item.key !== 'overview'}
              allowed={item.key === 'overview'}
              onClick={() => navigate(item.path)}
              badge={item.key === 'overview' && item.meetingCount > 0 ? item.meetingCount : null}
            />
          ))}
        </div>
        <div className="overview-side-nav__footer">
          {showDevTools ? (
            <NavLink
              icon={DevToolsIcon}
              label="Developer tools"
              caption="Dev tools"
              isActive={isDevToolsActive}
              disabled={navigationLocked}
              onClick={() => navigate(DEV_TOOLS_PATH)}
            />
          ) : null}
          {canSupport ? (
            <NavLink
              icon={SupportIcon}
              label="Support"
              caption="Support"
              isActive={isSupportActive}
              variant="support"
              disabled={navigationLocked}
              onClick={() => navigate('/app/overview/support')}
            />
          ) : null}
          <NavLink
            icon={LogoutIcon}
            label="Sign Out"
            caption="Sign out"
            isActive={false}
            variant="signOut"
            onClick={() => onSignOut?.()}
          />
        </div>
      </div>
    </nav>
  );
};

export default OverviewSideNav;
